import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Organization, OrganizationMemberRole } from "@domain/organization/organization";
import { useOrganizationMembers } from "@domain/user/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Spacer, row } from "@ui/components/layout";
import { DetailedMemberList } from "@ui/components/profile/community/detailedMemberList";
import { EditMembersModal } from "@ui/components/profile/community/editMembersModal";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
// import { mobile } from "../../../styles/utils";
import { createToast } from "@ui/toasts/utils";

interface OrganizationMembersProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
}

export const OrganizationMembers: React.FC<OrganizationMembersProps> = ({ organization, ...htmlProps }) => {
	const [displayedRole, setDisplayedRole] = useState<OrganizationMemberRole>(
		organization.type === "INSTITUTE" ? "CONTRIBUTOR" : "ADMINISTRATOR"
	);
	const { result: members, totalElements, fetchMore, loading } = useOrganizationMembers(organization.id, displayedRole);
	const alias = useOrganizationAlias();
	const isSuperAdmin = organization.userRoles.includes("SUPER_ADMINISTRATOR");
	const canEditMembers = !!alias;
	const { userService, currentProfileService, organizationService } = useServices();
	const currentProfile = useCurrentProfile();
	const { createModal } = useModalTools();
	const askDeleteConfirmation = useConfirmation(I18n.t(`members.${displayedRole}.delete.title`), I18n.t("delete"));
	const askDeleteSelfConfirmation = useConfirmation(
		I18n.t(`members.${displayedRole}.delete.self.title`),
		I18n.t("delete")
	);
	const availableTabs: OrganizationMemberRole[] =
		organization.type === "COMPANY"
			? ["ADMINISTRATOR"]
			: alias || isSuperAdmin
				? ["CONTRIBUTOR", "ADMINISTRATOR"]
				: ["CONTRIBUTOR"];

	const openMembersModal = (): void =>
		createModal((close) => (
			<EditMembersModal
				organizationId={organization.id}
				title={I18n.t(`members.${displayedRole}.add.title`)}
				close={close}
				onMembersChosen={async (members) => {
					try {
						await userService.addMembersToOrganization(organization.id, members, displayedRole);
						close();
						createToast(I18n.t("success"), I18n.t(`members.${displayedRole}.add.success`), "success");
					} catch {
						createToast(I18n.t("error"), I18n.t("members.add.error"), "error");
					}
				}}
			/>
		));

	return (
		<Container {...htmlProps}>
			{canEditMembers && (
				<StyledEditButton label={I18n.t(`members.${displayedRole}.add.button`)} onClick={openMembersModal} />
			)}
			<Tab>
				{availableTabs.map((role) => (
					<MemberTab selected={role === displayedRole} key={role} onClick={() => setDisplayedRole(role)}>
						{I18n.t(`members.${role}.list.title`, {
							count:
								role === "CONTRIBUTOR"
									? (organization.memberCounts.CONTRIBUTOR ?? 0)
									: (organization.memberCounts.ADMINISTRATOR ?? 0) +
										(organization.memberCounts.SUPER_ADMINISTRATOR ?? 0),
						})}
					</MemberTab>
				))}
			</Tab>
			<Hr space={1} />
			{totalElements === 0 && !!alias && (
				<PrimaryButton onClick={openMembersModal}>{I18n.t(`members.${displayedRole}.add.button`)}</PrimaryButton>
			)}
			<DetailedMemberList
				loading={loading}
				onEndReached={fetchMore}
				members={members}
				protectedMember={members.find((member) => (member as { role?: string }).role === "SUPER_ADMINISTRATOR")?.id}
				canRemoveItself
				deleteMember={
					canEditMembers
						? async (id: string) => {
								(id === currentProfile?.id ? askDeleteSelfConfirmation : askDeleteConfirmation)(async () => {
									try {
										await userService.deleteMemberFromOrganization(organization.id, id, displayedRole);
										if (id === currentProfile?.id) {
											currentProfileService.setAlias(null);
											organizationService.organizationStore.fetch(organization.id);
											if (organization.type === "INSTITUTE") {
												setDisplayedRole("CONTRIBUTOR");
											}
										}
										createToast(I18n.t("success"), I18n.t(`members.${displayedRole}.delete.success`), "success");
									} catch {
										createToast(I18n.t("error"), I18n.t(`members.delete.error`), "error");
									}
								});
							}
						: undefined
				}
			/>
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	position: relative;
	padding: 20px 0;
	> button {
		margin: 20px auto;
	}
`;

// const Title = styled.div`
// 	padding: 40px;
// 	${row(60)}
// 	@media ${mobile} {
// 		padding: 50px 20px 20px;
// 	}
// `;

const Hr = styled(Spacer)`
	border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
	margin-top: 20px !important;
`;

const Tab = styled.div`
	margin-left: 20px;
	background: #f3f4f6;
	width: 50%;
	padding: 10px;
	border-radius: 50px;
	${row(0)}
`;

const MemberTab = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>`
	cursor: pointer;
	${({ theme }) => theme.textStyles.light}
	color: ${({ theme, selected }) => (selected ? theme.colors.white : theme.colors.black)};
	background-color: ${({ theme, selected }) => (selected ? theme.colors.secondary : "#f3f4f6")};
	padding: 6px 25px;
	border-radius: 50px;
	font-weight: 600;
`;

const StyledEditButton = styled(RoundedPlusButton)`
	position: absolute;
	right: 20px;
	top: 20px;
`;
